import { useState, useEffect, useRef } from 'react';
import { PRODUCTS, LOCATIONS, formatProductQuantity, productUnitShort, inventoryCapacityUsed } from './game/constants.js';
import { applyClassicAction, createInitialState } from './game/engine.js';
import { HubClient } from './platform/hub-client.js';
import { getRuntimeConfig } from './platform/runtime.js';
import { AuthPortal } from './components/AuthPortal.jsx';
import { AccountSettings } from './components/AccountSettings.jsx';
import { ServerInfoModal } from './components/ServerInfoModal.jsx';
import { MaintenancePage } from './components/MaintenancePage.jsx';
import { LegacyGame } from './components/LegacyGame.jsx';
import { ResponsiveGame } from './components/ResponsiveGame.jsx';
import { AdventureLogDock } from './components/AdventureLogDock.jsx';
import { GameShellV2 } from './ui/v2/GameShellV2.jsx';
import { V2ErrorBoundary } from './ui/v2/V2ErrorBoundary.jsx';
import { V2SceneFrame } from './ui/v2/V2SceneFrame.jsx';
import { SceneEncounter, SceneOutcome } from './components/SceneEncounter.jsx';
import { CornerStoreScene } from './components/CornerStoreScene.jsx';
import { KenHouseScene } from './components/KenHouseScene.jsx';
import { PawnShopScene } from './components/PawnShopScene.jsx';
import { MikeysPlaceScene } from './components/MikeysPlaceScene.jsx';
import { RunModeSetup } from './components/RunModeSetup.jsx';
import { BootstrapScreen } from './components/BootstrapScreen.jsx';
import { DEFAULT_GAME_MODE_ID, FALLBACK_GAME_MODES, gameModeForState, gameOverReasonForState, normalizeGameModes, validGameModeId } from './game/modes.js';
import { wholeHealth } from './game/format.js';
import { DebtClearedCelebration } from './components/DebtClearedCelebration.jsx';
import { CareerLoanSharkScene } from './components/CareerLoanSharkScene.jsx';
import { CareerMilestoneCelebration } from './components/CareerMilestoneCelebration.jsx';
import { HustleModal } from './components/HustleModal.jsx';
import { CavemanHouseScene } from './components/CavemanHouseScene.jsx';
import { RobHippieScene } from './components/RobHippieScene.jsx';


const ACTION_LABELS = {
  travel: 'TRAVELING',
  buy: 'BUYING',
  sell: 'SELLING',
  sell_all: 'SELLING',
  pay_debt: 'PAYING DEBT',
  borrow: 'BORROWING',
  bank: 'BANKING',
  stash: 'MOVING PRODUCT',
  stash_all: 'MOVING PRODUCT',
  sell_barter: 'FENCING GOODS',
  resolve_event: 'RESOLVING ENCOUNTER',
  ack_debt_celebration: 'UNLOCKING LONG HAUL',
  career_buy_residence: 'MOVING UP',
  ack_career_milestone: 'CELEBRATING CAREER',
  corner_store_buy_smokes: 'BUYING SMOKES',
  corner_store_buy_beer: 'BUYING BEER',
  corner_store_buy_scratchers: 'SCRATCHING TICKETS',
  corner_store_leave: 'LEAVING STORE',
  corner_store_open_board: 'READING THE BOARD',
  drink_beer: 'DRINKING BEER',
  sell_smokes: 'SELLING SMOKES',
  special_character_sell: 'DEALING WITH MIKEY',
  hustle_search: 'DIGGING THROUGH TRASH',
  career_home_wait: 'KILLING AN HOUR',
  career_home_offer_accept: 'MAKING DESPERATION SALE',
  career_home_offer_decline: 'TURNING THEM AWAY',
  world_look_around: 'LOOKING AROUND',
  rob_smoke: 'SMOKING WITH ROB',
  rob_bong_rip: 'DOING ANOTHER BONG RIP',
  rob_end_session: 'TRYING TO STAND UP',
};

function productName(productId) {
  return PRODUCTS.find((product) => product.id === productId)?.name ?? productId ?? 'product';
}

function packAtLeast(packId, revision) {
  const match = /^classic-1\.(\d+)$/.exec(String(packId || ''));
  return !match || Number(match[1]) >= Number(revision);
}

function statChanges(previous, next) {
  const changes = [];
  const numeric = [
    ['cash', 'CASH', '$'],
    ['debt', 'DEBT', '$'],
    ['health', 'HEALTH', ''],
    ['streetRep', 'REP', ''],
    ['bankCash', 'BANK', '$'],
  ];
  for (const [key, label, prefix] of numeric) {
    const before = key === 'health' ? wholeHealth(previous?.[key]) : Number(previous?.[key] ?? 0);
    const after = key === 'health' ? wholeHealth(next?.[key]) : Number(next?.[key] ?? 0);
    const delta = after - before;
    if (Math.abs(delta) > 0.0001) changes.push({ label, delta, prefix });
  }
  for (const product of PRODUCTS) {
    const before = Number(previous?.inventory?.[product.id] ?? 0);
    const after = Number(next?.inventory?.[product.id] ?? 0);
    const delta = after - before;
    if (Math.abs(delta) > 0.0001) changes.push({ label: product.name.toUpperCase(), delta, suffix: productUnitShort(product.id) === 'g' ? 'g' : ` ${product.unitPlural}` });
  }
  for (const [key, label] of [['smokes', 'SMOKES'], ['beer', 'BEER']]) {
    const before = Number(previous?.streetInventory?.[key] ?? 0);
    const after = Number(next?.streetInventory?.[key] ?? 0);
    const delta = after - before;
    if (delta) changes.push({ label, delta });
  }
  return changes;
}

function newestActionMessage(previous, next) {
  const before = Array.isArray(previous?.messages) ? previous.messages : [];
  const after = Array.isArray(next?.messages) ? next.messages : [];
  const remaining = [...before];
  const added = [];
  for (const message of after) {
    const index = remaining.indexOf(message);
    if (index >= 0) remaining.splice(index, 1);
    else added.push(message);
  }
  return added.at(-1) || '';
}

function actionSignal(action, payload, previous, next, sequence) {
  const changes = statChanges(previous, next);
  const narrative = newestActionMessage(previous, next);
  let title = 'STREET UPDATE';
  let message = narrative;
  let category = 'adventure';
  if (action === 'buy') {
    title = 'PURCHASE COMPLETE'; category = 'trade';
    const before = Number(previous.inventory?.[payload.product_id] || 0);
    const after = Number(next.inventory?.[payload.product_id] || 0);
    const gained = Math.max(0, after - before);
    const spent = Math.max(0, Number(previous.cash || 0) - Number(next.cash || 0));
    message = `Bought ${formatProductQuantity(payload.product_id, gained)} of ${productName(payload.product_id)} for $${spent.toLocaleString()}.`;
  } else if (action === 'sell' || action === 'sell_all') {
    title = 'SALE COMPLETE'; category = 'trade';
    const before = Number(previous.inventory?.[payload.product_id] || 0);
    const after = Number(next.inventory?.[payload.product_id] || 0);
    const sold = Math.max(0, before - after);
    const earned = Math.max(0, Number(next.cash || 0) - Number(previous.cash || 0));
    message = `Sold ${formatProductQuantity(payload.product_id, sold)} of ${productName(payload.product_id)} for $${earned.toLocaleString()}.`;
  } else if (action === 'special_character_sell') {
    title = 'MIKEY BOUGHT IT'; category = 'trade';
    message ||= 'Mikey talks himself into another terrible deal.';
  } else if (action === 'hustle_search') {
    title = 'HUSTLE RESULT'; category = Number(next.health || 0) < Number(previous.health || 0) ? 'warning' : 'adventure';
    message ||= 'You finished digging through somebody else’s garbage.';
  } else if (action === 'career_home_wait') {
    const outcome = next?.career?.homeRecovery?.lastOutcome || {};
    title = outcome.title || 'STUCK AT HOME';
    category = outcome.type === 'bottom_barrel' || Number(outcome.payout || 0) > 0 ? 'success' : outcome.type === 'buyer' ? 'encounter' : 'warning';
    message ||= outcome.message || 'Another hour disappears while you wait for something useful to happen.';
  } else if (action === 'career_home_offer_accept') {
    title = 'DESPERATION SALE'; category = 'trade';
    message ||= next?.career?.homeRecovery?.lastOutcome?.message || 'Somebody finally showed up with cash.';
  } else if (action === 'career_home_offer_decline') {
    title = 'DEAL DECLINED'; category = 'warning';
    message ||= next?.career?.homeRecovery?.lastOutcome?.message || 'You sent the buyer away.';
  } else if (action === 'corner_store_buy_smokes' || action === 'corner_store_buy_beer') {
    title = 'CORNER STORE PURCHASE'; category = 'trade';
    message ||= 'The cashier shoved your purchase across the counter.';
  } else if (action === 'corner_store_buy_scratchers') {
    const payout = Number(next?.cornerStore?.lastScratcherPayout || 0);
    title = payout >= 2000 ? 'SCRATCHER JACKPOT' : payout >= 500 ? 'BIG SCRATCHER WIN' : payout > 0 ? 'SCRATCHER WINNER' : 'SCRATCHER LOSER';
    category = payout > 0 ? 'success' : 'warning';
    message ||= 'The silver dust settled.';
  } else if (action === 'corner_store_open_board') {
    title = 'BULLETIN BOARD'; category = 'system';
    message ||= 'The newest notices are pinned between graffiti and expired yard-sale ads.';
  } else if (action === 'drink_beer') {
    title = 'BEER BREAK'; category = Number(next.health || 0) < Number(previous.health || 0) ? 'warning' : 'adventure';
    message ||= 'You cracked open a beer.';
  } else if (action === 'corner_store_leave') {
    title = 'LEFT THE CORNER STORE'; category = 'adventure';
    message ||= 'You escaped the parking lot.';
  } else if (action === 'sell_smokes') {
    title = 'SMOKES SOLD'; category = 'trade';
    message ||= 'Somebody paid cash for the smokes.';
  } else if (action === 'travel') {
    const travel = next?.lastTravel || {};
    const isCareer = next?.gameModeId === 'career' || next?.gameMode?.id === 'career';
    if (isCareer && Number(travel.dailyExpenseUnpaid || 0) > 0) {
      title = "YOU'RE BROKE"; category = 'warning';
      message = `Daily expenses were $${Number(travel.dailyExpenseDue || 0).toLocaleString()}; you paid $${Number(travel.dailyExpensePaid || 0).toLocaleString()} and still owe $${Number(travel.dailyExpenseUnpaid || 0).toLocaleString()} for today. Make some money or take out a loan.`;
    } else {
      title = isCareer ? 'TRAVEL & EXPENSES' : 'TRAVEL'; category = 'adventure';
      const destination = LOCATIONS.find((location) => location.id === next.currentLocationId)?.name ?? 'your destination';
      message = isCareer
        ? `Arrived at ${destination}. Travel ${Number(travel.travelCost || 0) > 0 ? `-$${Number(travel.travelCost).toLocaleString()}` : 'FREE'}${Number(travel.dailyExpenseDue || 0) > 0 ? ` · Daily expenses -$${Number(travel.dailyExpensePaid || 0).toLocaleString()}` : ''}.`
        : (message || `Arrived at ${destination}.`);
    }
  } else if (action === 'resolve_event') {
    title = 'ENCOUNTER RESULT'; category = 'encounter';
    message ||= 'The encounter has been resolved.';
  } else if (action === 'bank') {
    title = payload.direction === 'deposit' ? 'CASH DEPOSITED' : 'CASH WITHDRAWN'; category = 'system';
    message ||= `${payload.direction === 'deposit' ? 'Deposited' : 'Withdrew'} $${Number(payload.amount || 0).toLocaleString()}.`;
  } else if (action === 'stash' || action === 'stash_all') {
    title = 'STASH UPDATED'; category = 'system';
    message ||= `${payload.direction === 'deposit' ? 'Stashed' : 'Retrieved'} ${productName(payload.product_id)}.`;
  } else if (action === 'pay_debt') {
    title = 'DEBT PAYMENT'; category = 'success';
    message ||= `Paid $${Number(payload.amount || 0).toLocaleString()} toward the debt.`;
  } else if (action === 'borrow') {
    title = 'LOAN SHARK'; category = 'warning';
    message ||= `Borrowed $${Number(payload.amount || 0).toLocaleString()}.`;
  } else if (action === 'sell_barter') {
    title = 'PAWN SHOP'; category = 'trade';
    message ||= 'The fenced goods changed hands.';
  }
  return { id: sequence, title, message, category, changes, day: Number(next.day || 1), createdAt: Date.now() };
}

function App() {
  const configRef = useRef(getRuntimeConfig());
  const hubRef = useRef(new HubClient(configRef.current));
  const hub = hubRef.current;
  const config = configRef.current;

  const [gameState, setGameState] = useState(() => createInitialState());
  const [runMeta, setRunMeta] = useState(null);
  const [onlineMode, setOnlineMode] = useState(config.onlineEnabled);
  const [session, setSession] = useState(hub.session);
  const [hubState, setHubState] = useState(config.onlineEnabled ? 'CONNECTING' : 'OFFLINE');
  const [hubMessage, setHubMessage] = useState('Checking The Dope Man server...');
  const [hubVersion, setHubVersion] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionLabel, setActionLabel] = useState('');
  const [uiSignal, setUiSignal] = useState(null);
  const [sceneOutcome, setSceneOutcome] = useState(null);
  const [dismissedSpecialtySceneId, setDismissedSpecialtySceneId] = useState('');
  const [lastRequestFailure, setLastRequestFailure] = useState(null);
  const signalSequence = useRef(0);
  const [authBusy, setAuthBusy] = useState(false);
  const [registration, setRegistration] = useState({ mode: 'invite', invite_required: true });
  const [gameModes, setGameModes] = useState(FALLBACK_GAME_MODES);
  const [selectedGameModeId, setSelectedGameModeId] = useState(DEFAULT_GAME_MODE_ID);
  const [runSetup, setRunSetup] = useState({ stage: 'choose', preview: null, error: '' });
  const [runLoading, setRunLoading] = useState(Boolean(hub.session));
  const [bootstrapState, setBootstrapState] = useState({ phase: config.onlineEnabled ? 'connecting' : 'ready', attempt: 1 });
  const bootstrapInFlightRef = useRef(null);
  const bootstrapGenerationRef = useRef(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [serverInfoOpen, setServerInfoOpen] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);
  const [serverInfoBusy, setServerInfoBusy] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const initialUiOverride = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ui') === 'v1' ? 'responsive' : '';
  const [sessionInterfaceOverride, setSessionInterfaceOverride] = useState(initialUiOverride);
  const [adventureDockUnread, setAdventureDockUnread] = useState(0);
  const adventureDockLatestRef = useRef('');
  const [uiPreferences, setUiPreferences] = useState({
    defaults: { interface: 'responsive', theme: 'classic' },
    preferences: {
      interface: 'default', theme: 'default', toast_notifications: true,
      narration_bar: true, toast_duration: 'normal', reduced_motion: false,
      display_density: 'comfortable', text_size: 'normal',
      adventure_log_enabled: true, adventure_log_recent_count: 8,
      adventure_log_detail: 'compact', adventure_log_show_routine: false,
      adventure_log_panel: 'compact', adventure_log_filter: 'all',
    },
    effective: {
      interface: 'responsive', theme: 'classic', toast_notifications: true,
      narration_bar: true, toast_duration: 'normal', reduced_motion: false,
      display_density: 'comfortable', text_size: 'normal',
      adventure_log_enabled: true, adventure_log_recent_count: 8,
      adventure_log_detail: 'compact', adventure_log_show_routine: false,
      adventure_log_panel: 'compact', adventure_log_filter: 'all',
    },
    available: {
      interfaces: ['responsive', 'legacy'], themes: ['classic', 'dark'],
      toast_durations: ['short', 'normal', 'long'],
      display_densities: ['comfortable', 'compact'], text_sizes: ['small', 'normal', 'large'],
      adventure_log_details: ['compact', 'detailed'], adventure_log_panels: ['hidden', 'compact', 'expanded'], adventure_log_filters: ['all', 'important', 'travel', 'market', 'world'], adventure_log_recent_count: { minimum: 4, maximum: 20 },
    },
    themes_require_responsive: true,
  });
  const [promptModal, setPromptModal] = useState(null);
  const [careerLoanOpen, setCareerLoanOpen] = useState(false);

  useEffect(() => {
    if (dismissedSpecialtySceneId && gameState.currentLocationId !== dismissedSpecialtySceneId) {
      setDismissedSpecialtySceneId('');
    }
  }, [gameState.currentLocationId, dismissedSpecialtySceneId]);
  const [hustleOpen, setHustleOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('1000');
  const [selectedWeights, setSelectedWeights] = useState(() => Object.fromEntries(
    PRODUCTS.map((product) => [product.id, Number(product.options?.[0]?.weight || 1)]),
  ));

  async function refreshServerInfo({ open = false } = {}) {
    if (open) setServerInfoOpen(true);
    setServerInfoBusy(true);
    try {
      const info = await hub.serverInfo();
      setServerInfo(info);
      setMaintenance(info?.maintenance?.enabled ? info.maintenance : null);
      return info;
    } catch (error) {
      setHubMessage(error.message);
      return null;
    } finally {
      setServerInfoBusy(false);
    }
  }

  useEffect(() => {
    const handler = (event) => setMaintenance({
      enabled: true,
      title: event.detail?.title,
      message: event.detail?.message,
      reason: event.detail?.reason,
      enabled_at: event.detail?.enabled_at,
    });
    window.addEventListener('dopeman-maintenance', handler);
    return () => window.removeEventListener('dopeman-maintenance', handler);
  }, []);

  const bootstrapDelay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  function bootstrapIsTransient(error) {
    return error?.code === 'HUB_OFFLINE' || Number(error?.status || 0) === 0 || Number(error?.status || 0) >= 500;
  }

  async function runBootstrap({ manual = false } = {}) {
    if (!config.onlineEnabled) {
      setBootstrapState({ phase: 'ready', attempt: 1 });
      return true;
    }
    const activeBootstrap = bootstrapInFlightRef.current;
    if (activeBootstrap?.generation === bootstrapGenerationRef.current) return activeBootstrap.promise;

    const generation = ++bootstrapGenerationRef.current;
    const task = (async () => {
      await hub.hydrateNativeStorage();
      if (generation !== bootstrapGenerationRef.current) return false;
      setSession(hub.session);
      setHubState('CONNECTING');
      setHubMessage('Connecting securely…');
      setBootstrapState({ phase: 'connecting', attempt: 1 });

      const delays = manual ? [0, 450, 1100] : [0, 300, 800, 1600];
      let lastError = null;
      for (let index = 0; index < delays.length; index += 1) {
        if (delays[index]) await bootstrapDelay(delays[index]);
        if (generation !== bootstrapGenerationRef.current) return false;
        const attempt = index + 1;
        setBootstrapState({ phase: 'connecting', attempt });
        try {
          const publicInfo = await hub.serverInfo();
          if (generation !== bootstrapGenerationRef.current) return false;
          setServerInfo(publicInfo);
          if (publicInfo?.maintenance?.enabled) {
            setMaintenance(publicInfo.maintenance);
            setHubState('MAINTENANCE');
            setHubMessage('Server maintenance in progress.');
            setRunLoading(false);
            setBootstrapState({ phase: 'ready', attempt });
            return true;
          }

          const [probe, registrationStatus, modeCatalog] = await Promise.all([
            hub.probe(),
            hub.registrationStatus(),
            hub.gameModes().catch(() => ({ default_game_mode_id: DEFAULT_GAME_MODE_ID, modes: FALLBACK_GAME_MODES })),
          ]);
          if (generation !== bootstrapGenerationRef.current) return false;
          const availableModes = normalizeGameModes(modeCatalog);
          setGameModes(availableModes);
          setSelectedGameModeId(validGameModeId(availableModes, modeCatalog?.default_game_mode_id));
          setRegistration(registrationStatus);
          setHubState(probe.health?.ok ? 'ONLINE' : 'DEGRADED');
          setHubVersion(probe.health?.server_version || probe.health?.version || probe.game?.server_version || '');
          setHubMessage('Connected.');

          if (hub.authenticated) {
            setSession(hub.session);
            try {
              await loadOnlineRun();
            } catch (sessionError) {
              if (!hub.authenticated || Number(sessionError?.status || 0) === 401) {
                setSession(null);
                applyRun(null);
                setHubState('ONLINE');
                setHubMessage('Your saved session expired. Sign in again.');
              } else {
                throw sessionError;
              }
            }
          } else {
            setSession(null);
            setRunLoading(false);
          }
          setBootstrapState({ phase: 'ready', attempt });
          return true;
        } catch (error) {
          lastError = error;
          if (!bootstrapIsTransient(error) || index === delays.length - 1) break;
        }
      }

      if (!hub.authenticated) setSession(null);
      setRunLoading(false);
      setHubState('OFFLINE');
      setHubMessage('The game server is temporarily unavailable.');
      setBootstrapState({ phase: 'offline', attempt: delays.length, code: lastError?.code || 'UNAVAILABLE' });
      return false;
    })().finally(() => {
      if (bootstrapInFlightRef.current?.promise === task) bootstrapInFlightRef.current = null;
    });
    bootstrapInFlightRef.current = { generation, promise: task };
    return task;
  }

  useEffect(() => {
    runBootstrap();
    return () => { bootstrapGenerationRef.current += 1; };
  }, []);

  function applyRun(run) {
    if (!run?.state) {
      setRunMeta(null);
      setGameState(createInitialState());
      setSceneOutcome(null);
      return;
    }
    setRunMeta({
      runId: run.run_id,
      revision: run.revision,
      status: run.status,
      ruleset: run.ruleset,
    });
    setGameState({ ...run.state, health: wholeHealth(run.state.health) });
  }

  function chooseRunMode(modeId) {
    const selected = validGameModeId(gameModes, modeId);
    setSelectedGameModeId(selected);
    setRunSetup({ stage: 'choose', preview: null, error: '' });
  }

  async function reviewRunMode(modeId = selectedGameModeId) {
    const selected = validGameModeId(gameModes, modeId);
    setSelectedGameModeId(selected);
    setRunSetup({ stage: 'preview-loading', preview: null, error: '' });
    try {
      const response = await hub.startPreview(selected);
      if (!response?.preview?.preview_token || !response.preview?.preview_fingerprint) {
        throw new Error('The server did not return a complete run preview. No run was created.');
      }
      setRunSetup({ stage: 'confirm', preview: response.preview, error: '' });
      setHubMessage(`${response.preview.game_mode?.title || 'Run'} is ready for review.`);
      return response.preview;
    } catch (error) {
      setRunSetup({ stage: 'error', preview: null, error: error.message });
      setHubMessage(error.message);
      return null;
    }
  }

  async function startSelectedRun() {
    const originalPreview = runSetup.preview;
    if (!originalPreview) {
      await reviewRunMode(selectedGameModeId);
      return;
    }
    setRunSetup((previous) => ({ ...previous, stage: 'starting', error: '' }));
    try {
      const refreshed = await hub.startPreview(selectedGameModeId);
      const nextPreview = refreshed?.preview;
      if (!nextPreview?.preview_token || !nextPreview?.preview_fingerprint) {
        throw new Error('The server could not re-check the run preview. No run was created.');
      }
      if (
        nextPreview.preview_fingerprint !== originalPreview.preview_fingerprint
        || nextPreview.game_mode?.id !== selectedGameModeId
      ) {
        setRunSetup({ stage: 'confirm', preview: nextPreview, error: '' });
        setHubMessage('The run rules changed while you were reviewing them. Check the refreshed preview before starting.');
        return;
      }
      const response = await hub.startRun(selectedGameModeId, nextPreview.preview_token);
      if (!response?.run?.state) throw new Error('The server returned an incomplete run. Reload before taking an action.');
      applyRun(response.run);
      setRunSetup({ stage: 'choose', preview: null, error: '' });
      setHubState('ONLINE');
      setLastRequestFailure(null);
      const mode = gameModeForState(response.run.state);
      setHubMessage(`${mode.title} started · ${response.run.ruleset} · revision ${response.run.revision}`);
    } catch (error) {
      // If the response was lost after commit, the active-run read safely reconciles it.
      try {
        const current = await hub.currentRun();
        if (current?.run?.status === 'active' && current.run.state) {
          applyRun(current.run);
          setRunSetup({ stage: 'choose', preview: null, error: '' });
          setHubMessage('Run loaded. The server committed it even though the first response got lost.');
          return;
        }
      } catch {
        // Keep the original failure below.
      }
      setRunSetup({ stage: 'error', preview: originalPreview, error: error.message });
      setHubMessage(error.message);
    }
  }

  async function loadOnlineRun(preferredModeId = '', autoPreview = false) {
    setRunLoading(true);
    // Never leave a previous account/run visible while the current identity is loading.
    applyRun(null);
    try {
      const [preferencesResult, current] = await Promise.all([
        hub.uiPreferences(),
        hub.currentRun(),
      ]);
      if (preferencesResult?.ui) setUiPreferences(preferencesResult.ui);
      setHubState('ONLINE');
      setLastRequestFailure(null);
      if (current?.run?.state) {
        applyRun(current.run);
        setRunSetup({ stage: 'choose', preview: null, error: '' });
        setHubMessage(`Signed in as ${hub.session?.player?.alias ?? 'player'} · saved online · revision ${current.run.revision}`);
        return current.run;
      }
      const selected = validGameModeId(gameModes, preferredModeId || selectedGameModeId);
      setSelectedGameModeId(selected);
      setRunSetup({ stage: 'choose', preview: null, error: '' });
      setHubMessage('No active run was found. Choose the rules for your new run.');
      if (autoPreview) await reviewRunMode(selected);
      return null;
    } finally {
      setRunLoading(false);
    }
  }

  function clearRunScopedPresentationCaches(runId) {
    if (typeof window === 'undefined' || !runId) return;
    const suffix = `.${runId}`;
    for (const key of Object.keys(window.localStorage)) {
      if ((key.startsWith('the-dope-man.activity.') || key.startsWith('the-dope-man.view.')) && key.endsWith(suffix)) {
        window.localStorage.removeItem(key);
      }
    }
  }

  async function handleSelfReset(run, preview) {
    const oldRunId = runMeta?.runId;
    clearRunScopedPresentationCaches(oldRunId);
    setSceneOutcome(null);
    setPromptModal(null);
    applyRun(run);
    signalSequence.current += 1;
    setUiSignal({
      id: signalSequence.current,
      title: 'FRESH RUN LOADED',
      message: `The old run is gone. ${preview?.event_pack?.title || run.ruleset || 'The current event pack'} is now active for this run.`,
      category: 'system',
      changes: [],
      day: Number(run.state?.day || 1),
      createdAt: Date.now(),
      persistent: true,
    });
    setHubState('ONLINE');
    setLastRequestFailure(null);
    setHubMessage(`Fresh run loaded · ${run.ruleset} · revision ${run.revision}`);
  }

  const handleLogin = async ({ email, password }) => {
    setAuthBusy(true);
    setHubMessage('Checking your player account...');
    try {
      const nextSession = await hub.login(email, password);
      setSession(nextSession);
      setHubState('ONLINE');
      await loadOnlineRun();
    } catch (error) {
      setHubState(error.code === 'HUB_OFFLINE' ? 'OFFLINE' : 'AUTH ERROR');
      setHubMessage(error.code === 'HUB_OFFLINE' ? 'Connection interrupted. Try again in a moment.' : 'Sign-in was not accepted. Check your credentials and try again.');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRegister = async ({ email, alias, password, inviteCode, gameModeId }) => {
    setAuthBusy(true);
    setHubMessage('Creating your Dope Man player account...');
    try {
      const nextSession = await hub.register({ email, alias, password, inviteCode });
      setSession(nextSession);
      setHubState('ONLINE');
      setHubMessage(`Player ${nextSession.player?.alias ?? alias} created. Preparing the selected run...`);
      setSelectedGameModeId(validGameModeId(gameModes, gameModeId));
      await loadOnlineRun(gameModeId, true);
    } catch (error) {
      setHubState(error.code === 'HUB_OFFLINE' ? 'OFFLINE' : 'AUTH ERROR');
      setHubMessage(error.code === 'HUB_OFFLINE' ? 'Connection interrupted. Try again in a moment.' : 'Registration could not be completed with those details.');
    } finally {
      setAuthBusy(false);
    }
  };

  const playOffline = () => {
    setOnlineMode(false);
    setHubState('OFFLINE');
    setHubMessage('Local Classic run · not synchronized');
    setRunMeta(null);
    setRunSetup({ stage: 'choose', preview: null, error: '' });
    setGameState(createInitialState());
  };

  const reconnect = async () => {
    await runBootstrap({ manual: true });
  };

  const signOut = async () => {
    await hub.logout();
    setSession(null);
    applyRun(null);
    setRunSetup({ stage: 'choose', preview: null, error: '' });
    setSettingsOpen(false);
    setUiPreferences((previous) => ({
      ...previous,
      preferences: { ...previous.preferences, interface: 'default', theme: 'default' },
      effective: { ...previous.effective, interface: 'responsive', theme: 'classic' },
    }));
    setHubState('ONLINE');
    setHubMessage('Signed out. Use your existing player account to continue.');
  };

  const performAction = async (action, payload = {}) => {
    if (actionBusy) return false;
    const previousState = gameState;
    const previousEvent = previousState.activeEvent;
    setActionBusy(true);
    setActionLabel(ACTION_LABELS[action] ?? 'SAVING');
    try {
      let nextState;
      if (onlineMode && session) {
        if (!runMeta) {
          setRunSetup({ stage: 'choose', preview: null, error: '' });
          setHubState('ONLINE');
          setHubMessage('No active run exists. Choose a game mode before taking street actions.');
          return false;
        }
        const response = await hub.action(action, payload, runMeta.revision);
        if (!response?.run?.state || Number(response.run.revision) <= Number(runMeta.revision)) {
          throw Object.assign(new Error('The server returned stale run data; the action was not accepted.'), {
            code: 'STALE_RUN_RESPONSE', status: 409, path: '/game/classic/runs/current/action',
          });
        }
        nextState = response.run.state;
        applyRun(response.run);
        setHubState('ONLINE');
        setLastRequestFailure(null);
        setHubMessage(`Saved · revision ${response.run.revision}`);
        if (action === 'travel') {
          setSelectedWeights({ bud: 3.5, dabs: 1, shrooms: 3.5, meth: 1, heroin: 1 });
        }
      } else {
        nextState = applyClassicAction(previousState, action, payload);
        nextState = { ...nextState, health: wholeHealth(nextState.health) };
        setGameState(nextState);
        setHubMessage('Local Classic run · not synchronized');
      }
      signalSequence.current += 1;
      const signal = actionSignal(action, payload, previousState, nextState, signalSequence.current);
      setUiSignal(signal);
      if (
        action === 'resolve_event' &&
        (previousEvent?.type === 'scene' || previousEvent?.presentationMode === 'scene') &&
        !nextState.activeEvent
      ) {
        setSceneOutcome({
          title: previousEvent.title ?? 'Scene Encounter',
          message: signal.message,
          changes: signal.changes,
        });
      }
      return true;
    } catch (error) {
      setLastRequestFailure({
        endpoint: error.path || '/game/classic/runs/current/action',
        status: Number(error.status || 0), code: error.code || 'CLIENT_ERROR', message: error.message,
        at: new Date().toISOString(),
      });
      if (error.code === 'RUN_CONFLICT' && onlineMode && session) {
        try {
          const current = await hub.currentRun();
          applyRun(current.run);
          setHubMessage('Run was changed elsewhere and has been reloaded. Try the action again.');
          signalSequence.current += 1;
          setUiSignal({ id: signalSequence.current, title: 'RUN RELOADED', message: 'This run changed on another device and was safely reloaded. Try the action again.', category: 'warning', changes: [], day: Number(current.run?.state?.day || 1), createdAt: Date.now(), persistent: true });
        } catch (reloadError) {
          applyRun(null);
          setHubMessage(reloadError.message);
        }
      } else if (error.code === 'RUN_NOT_FOUND' && onlineMode && session) {
        applyRun(null);
        setRunSetup({ stage: 'choose', preview: null, error: '' });
        setHubMessage('The server has no active run. Choose Classic, Long Haul, or Career; the rejected action was not applied.');
      } else {
        if (error.status === 401) {
          setSession(null);
          applyRun(null);
        }
        setHubMessage(error.message);
        signalSequence.current += 1;
        const brokeTravel = error.code === 'CAREER_TRAVEL_CASH_REQUIRED';
        setUiSignal({ id: signalSequence.current, title: brokeTravel ? "YOU'RE BROKE" : 'ACTION FAILED', message: brokeTravel ? `${error.message} Your trip was not charged and your location did not change.` : error.message, category: brokeTravel ? 'warning' : 'error', changes: [], day: Number(previousState.day || 1), createdAt: Date.now(), persistent: true });
      }
      return false;
    } finally {
      setActionBusy(false);
      setActionLabel('');
    }
  };

  const restartRun = async () => {
    if (onlineMode && session) {
      const currentModeId = gameModeForState(gameState).id;
      applyRun(null);
      setSelectedGameModeId(validGameModeId(gameModes, currentModeId));
      setRunSetup({ stage: 'choose', preview: null, error: '' });
      setHubMessage('Choose the rules for your next run. The completed run remains safely stored.');
      return;
    }
    setGameState(createInitialState());
  };

  const preferredInterface = onlineMode && session
    ? (uiPreferences.effective?.interface ?? 'responsive')
    : 'responsive';
  const effectiveInterface = sessionInterfaceOverride || preferredInterface;
  const effectiveV2Theme = uiPreferences.effective?.v2_theme ?? 'dope_man_classic';
  const effectiveTheme = effectiveInterface !== 'legacy'
    ? (uiPreferences.effective?.theme ?? 'classic')
    : 'classic';

  const effectivePresentation = uiPreferences.effective ?? {};

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.dataset.theme = effectiveTheme;
    document.documentElement.dataset.v2Theme = effectiveV2Theme;
    document.documentElement.dataset.interface = effectiveInterface;
    document.documentElement.dataset.density = effectivePresentation.display_density ?? 'comfortable';
    document.documentElement.dataset.textSize = effectivePresentation.text_size ?? 'normal';
    document.documentElement.dataset.motion = effectivePresentation.reduced_motion ? 'reduced' : 'full';
    return () => {
      delete document.documentElement.dataset.theme;
      delete document.documentElement.dataset.v2Theme;
      delete document.documentElement.dataset.interface;
      delete document.documentElement.dataset.density;
      delete document.documentElement.dataset.textSize;
      delete document.documentElement.dataset.motion;
    };
  }, [effectiveInterface, effectiveTheme, effectiveV2Theme, effectivePresentation.display_density, effectivePresentation.text_size, effectivePresentation.reduced_motion]);

  useEffect(() => {
    const latestId = String(gameState?.adventureLog?.entries?.[0]?.id || '');
    if (!latestId) return;
    if (!adventureDockLatestRef.current) { adventureDockLatestRef.current = latestId; return; }
    if (latestId !== adventureDockLatestRef.current) {
      if ((uiPreferences.effective?.adventure_log_panel || 'compact') === 'hidden') {
        setAdventureDockUnread((count) => Math.min(99, count + 1));
      }
      adventureDockLatestRef.current = latestId;
    }
  }, [gameState?.adventureLog?.entries?.[0]?.id, uiPreferences.effective?.adventure_log_panel]);

  const setAdventureDockPreference = async (key, value) => {
    const camel = key === 'adventure_log_panel' ? 'adventureLogPanel' : 'adventureLogFilter';
    const previous = uiPreferences;
    const next = {
      ...previous,
      preferences: { ...previous.preferences, [key]: value },
      effective: { ...previous.effective, [key]: value },
    };
    setUiPreferences(next);
    if (key === 'adventure_log_panel' && value !== 'hidden') setAdventureDockUnread(0);
    if (onlineMode && session) {
      try {
        const response = await hub.updateUiPreferences({ [camel]: value });
        if (response?.ui) setUiPreferences(response.ui);
      } catch (error) {
        setUiPreferences(previous);
        setHubMessage(error.message);
      }
    }
  };

  const switchToStableInterface = (view = 'street') => {
    setSessionInterfaceOverride('responsive');
    if (typeof window !== 'undefined') window.location.hash = view;
  };

  const returnToPreviewInterface = () => {
    setSessionInterfaceOverride('');
    if (typeof window !== 'undefined' && window.location.search.includes('ui=v1')) {
      const url = new URL(window.location.href); url.searchParams.delete('ui'); window.history.replaceState({}, '', url);
    }
  };

  const getCurrentLocation = () => LOCATIONS.find(location => location.id === gameState.currentLocationId);
  const getInventoryCount = () => inventoryCapacityUsed(gameState.inventory);

  const travel = (locationId, travelMode = 'normal') => performAction('travel', { location_id: locationId, travel_mode: travelMode });
  const handleBuy = (productId) => performAction('buy', { product_id: productId, weight: selectedWeights[productId] });
  const handleSell = (productId) => performAction('sell', { product_id: productId, weight: selectedWeights[productId] });
  const handleSellAll = (productId) => performAction('sell_all', { product_id: productId });

  const handlePromptSubmit = () => {
    const amount = Number.parseInt(promptInput, 10);
    if (Number.isFinite(amount) && amount > 0) {
      performAction(promptModal.type === 'pay' ? 'pay_debt' : 'borrow', { amount });
    }
    setPromptModal(null);
  };

  const payDebt = () => {
    if (gameModeForState(gameState).id === 'career') { setCareerLoanOpen(true); return; }
    if (gameState.cash > 0 && gameState.debt > 0) {
      const maximum = Math.min(gameState.cash, gameState.debt);
      setPromptInput(maximum.toString());
      setPromptModal({ type: 'pay', title: 'Pay Down Debt', desc: `How much do you want to pay? (Max: $${maximum})` });
    }
  };

  const borrowMoney = () => {
    if (gameModeForState(gameState).id === 'career') { setCareerLoanOpen(true); return; }
    const multiplier = Number(gameState.serverRules?.loan_debt_multiplier ?? 1.5);
    const interest = Math.max(0, Math.round((multiplier - 1) * 100));
    setPromptInput('1000');
    setPromptModal({
      type: 'borrow',
      title: 'Borrow Cash',
      desc: `How much do you want to borrow? The Loan Shark adds ${interest}% to your debt.`,
    });
  };

  const careerBorrow = async (amount) => { await performAction('borrow', { amount }); };
  const careerPay = async (amount) => { await performAction('pay_debt', { amount }); };
  const careerBuyResidence = (residenceId) => performAction('career_buy_residence', { residence_id: residenceId });
  const acknowledgeCareerMilestone = () => performAction('ack_career_milestone');

  const bankAction = (amount, isDeposit) => performAction('bank', {
    amount,
    direction: isDeposit ? 'deposit' : 'withdraw',
  });

  const stashAction = (productId, isDeposit) => performAction('stash', {
    product_id: productId,
    weight: selectedWeights[productId],
    direction: isDeposit ? 'deposit' : 'withdraw',
  });

  const stashAllAction = (productId, isDeposit) => performAction('stash_all', {
    product_id: productId,
    direction: isDeposit ? 'deposit' : 'withdraw',
  });

  const sellBarter = (itemKey) => performAction('sell_barter', { item_id: itemKey });
  const buyTravelGear = (itemKey) => performAction('buy_travel_gear', { item_id: itemKey });
  const buySmokes = (quantity) => performAction('corner_store_buy_smokes', { quantity });
  const buyBeer = (quantity) => performAction('corner_store_buy_beer', { quantity });
  const buyScratchers = (quantity) => performAction('corner_store_buy_scratchers', { quantity });
  const leaveCornerStore = () => performAction('corner_store_leave');
  const openCornerStoreBoard = () => performAction('corner_store_open_board');
  const drinkBeer = () => performAction('drink_beer');
  const sellSmokes = (quantity) => performAction('sell_smokes', { quantity });
  const kenSellBud = (amount) => performAction('ken_sell_bud', { amount });
  const kenBuyMeth = (amount) => performAction('ken_buy_meth', { amount });
  const kenTradeJunk = (itemId) => performAction('ken_trade_junk', { item_id: itemId });
  const kenLookAround = () => performAction('ken_look_around');
  const kenGiveWifeBeer = () => performAction('ken_give_wife_beer');
  const mikeySell = (productId, amount, paymentType = 'cash') => performAction('special_character_sell', { product_id: productId, amount, payment_type: paymentType });
  const hustleSearch = (hustleId, siteId) => performAction('hustle_search', { hustle_id: hustleId, site_id: siteId });
  const careerHomeWait = () => performAction('career_home_wait');
  const careerHomeOfferAccept = () => performAction('career_home_offer_accept');
  const careerHomeOfferDecline = () => performAction('career_home_offer_decline');
  const robSmoke = () => performAction('rob_smoke');
  const robBongRip = () => performAction('rob_bong_rip');
  const robEndSession = () => performAction('rob_end_session');
  const acknowledgeDebtCelebration = () => performAction('ack_debt_celebration');
  const loadLeaderboard = (filters) => hub.leaderboard(filters);
  const setLeaderboardVisibility = (visible) => hub.updateLeaderboardPreferences(visible);

  const renderPromptModal = () => {
    if (!promptModal) return null;
    return (
      <div className="game-over-overlay" style={{ zIndex: 300 }}>
        <div className="glass-panel game-over-modal">
          <h2 style={{color: 'var(--accent-blue)'}}>{promptModal.title}</h2>
          <p style={{marginBottom: '1rem'}}>{promptModal.desc}</p>
          <input 
            type="number" 
            value={promptInput}
            onChange={(e) => setPromptInput(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', background: 'rgba(0,0,0,0.5)', color: 'white', border: '1px solid var(--panel-border)', borderRadius: '4px' }}
            autoFocus
          />
          <div style={{display: 'flex', gap: '1rem'}}>
            <button className="btn buy" style={{flex: 1}} onClick={handlePromptSubmit}>Submit</button>
            <button className="btn sell" style={{flex: 1}} onClick={() => setPromptModal(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const renderEventModal = () => {
    if (!gameState.activeEvent) return null;
    const ev = gameState.activeEvent;
    if (ev.type === 'scene' || ev.presentationMode === 'scene') {
      return (
        <SceneEncounter
          event={ev}
          busy={actionBusy}
          onChoose={(choice) => performAction('resolve_event', { choice })}
        />
      );
    }
    
    return (
      <div className="game-over-overlay" style={{ zIndex: 200 }}>
        <div className="glass-panel game-over-modal">
          {ev.type === 'home_raid' && (
            <>
              <h2 style={{color: 'var(--accent-red)'}}>HOME RAIDED!</h2>
              <p style={{marginBottom: '2rem'}}>The cops kicked down the door to your safehouse!</p>
              <button className="btn" style={{width: '100%'}} onClick={() => performAction('resolve_event', { choice: 'accept' })}>See Damage</button>
            </>
          )}
          {ev.type === 'mugging' && (
            <>
              <h2 style={{color: 'var(--accent-red)'}}>YOU ARE BEING MUGGED!</h2>
              <p style={{marginBottom: '2rem'}}>Some thugs cornered you in an alley...</p>
              <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                <button className="btn" onClick={() => performAction('resolve_event', { choice: 'fight' })}>Fight / Run (Take Damage)</button>
                <button className="btn sell" onClick={() => performAction('resolve_event', { choice: 'submit' })}>Hand it over (No damage; losses scale with difficulty)</button>
              </div>
            </>
          )}
          {ev.type === 'police' && (
            <>
              <h2 style={{color: 'var(--accent-red)'}}>POLICE SIRENS!</h2>
              <p style={{marginBottom: '2rem'}}>The cops pull you over. They look thirsty for a bribe...</p>
              <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                <button className="btn" onClick={() => performAction('resolve_event', { choice: 'accept' })}>Accept Fate (Confiscation severity scales with difficulty)</button>
                <button className="btn buy" onClick={() => performAction('resolve_event', { choice: 'bribe' })}>Attempt Bribe (50% chance; failure severity scales with difficulty)</button>
              </div>
            </>
          )}
          {ev.type === 'buyer_encounter' && (
            <>
              <h2 style={{color: 'var(--accent-blue)'}}>BUYER ENCOUNTER</h2>
              <p style={{marginBottom: '2rem'}}>
                A buyer wants to grab <strong>{ev.amountLabel || formatProductQuantity(ev.product, ev.amount)}</strong> of {PRODUCTS.find(p=>p.id===ev.product)?.name} off you. 
                {ev.isTrade
                  ? ` They have no cash, but are offering a ${ev.tradeItem} in trade.`
                  : ` They are offering $${Number(ev.cashOffer || 0).toLocaleString()} in cash${Number(ev.cashPremiumPercent || 0) > 0 ? ` — a ${Number(ev.cashPremiumPercent)}% Long Haul debt-phase premium.` : '.'}`}
              </p>
              <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                <button className="btn" onClick={() => performAction('resolve_event', { choice: 'fair' })}>Play Fair (Normal deal)</button>
                <button className="btn buy" onClick={() => performAction('resolve_event', { choice: 'extra' })}>Give 5% Extra (Gain Rep)</button>
                <button className="btn sell" onClick={() => performAction('resolve_event', { choice: 'short' })}>Short them 5% (Risk getting caught)</button>
              </div>
            </>
          )}
          {ev.type === 'smokes_buyer' && (
            <>
              <h2 style={{color: 'var(--accent-gold)'}}>CIGARETTE BUYER</h2>
              <p style={{marginBottom: '2rem'}}>
                {ev.isBarter
                  ? <>Somebody wants <strong>{ev.quantity} packs</strong> of smokes and offers {ev.barterLabel || 'some suspicious property'}.</>
                  : <>A neighborhood buyer offers <strong>${Number(ev.cashOffer || 0).toLocaleString()}</strong> for <strong>{ev.quantity} pack{Number(ev.quantity) === 1 ? '' : 's'}</strong> of smokes (${Number(ev.offerPerPack || 0)} each).</>}
              </p>
              <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                <button className="btn buy" disabled={actionBusy} onClick={() => performAction('resolve_event', { choice: 'accept' })}>Take the deal</button>
                <button className="btn sell" disabled={actionBusy} onClick={() => performAction('resolve_event', { choice: 'decline' })}>Pass</button>
              </div>
            </>
          )}
          {ev.type === 'travel_micro' && (
            <>
              <h2 style={{color: 'var(--accent-gold)'}}>{ev.title || 'ROADSIDE INCIDENT'}</h2>
              <p style={{marginBottom: '1.2rem'}}>{ev.body}</p>
              <div className="roadside-choices">
                {(ev.choices || []).map((choice) => <button key={choice.id} className="btn" disabled={actionBusy || !choice.available} onClick={() => performAction('resolve_event', { choice: choice.id })}><strong>{choice.label}</strong>{!choice.available && choice.requirement && <small>{choice.requirement}</small>}</button>)}
              </div>
            </>
          )}
          {ev.type === 'gear_theft' && (
            <>
              <h2 style={{color: 'var(--accent-red)'}}>{ev.title || 'YOUR RIDE IS GONE'}</h2>
              <p style={{marginBottom: '1rem'}}>{ev.body || 'Something you parked outside disappeared.'}</p>
              <p style={{marginBottom: '2rem', opacity: .78}}>You are still at {ev.locationName || 'this location'} and can continue on foot.</p>
              <button className="btn sell" style={{width: '100%'}} disabled={actionBusy} onClick={() => performAction('resolve_event', { choice: 'continue' })}>Well, shit.</button>
            </>
          )}
          {ev.type === 'caveman_whore' && (
            <>
              <h2 style={{color: 'var(--accent-red)'}}>A WILD ENCOUNTER</h2>
              <p style={{marginBottom: '2rem'}}>A fat whore from the trailer park demands a threesome...</p>
              <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                <button className="btn" onClick={() => performAction('resolve_event', { choice: 'reject' })}>Reject her (Take 5 damage)</button>
                <button className="btn sell" onClick={() => performAction('resolve_event', { choice: 'submit' })}>Submit (Lose street rep)</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const gameOverReason = gameOverReasonForState(gameState);
  const gameOver = Boolean(gameOverReason);

  if (onlineMode && bootstrapState.phase !== 'ready') {
    return <BootstrapScreen phase={bootstrapState.phase} attempt={bootstrapState.attempt} onRetry={reconnect} onPlayOffline={playOffline} />;
  }

  if (onlineMode && !session) {
    return (
      <AuthPortal
        hubState={hubState}
        hubMessage={hubMessage}
        config={config}
        registration={registration}
        gameModes={gameModes}
        selectedGameModeId={selectedGameModeId}
        onGameModeSelect={chooseRunMode}
        busy={authBusy}
        loadingSavedRun={false}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onPlayOffline={playOffline}
      />
    );
  }

  if (onlineMode && session && !runMeta) {
    return (
      <RunModeSetup
        session={session}
        modes={gameModes}
        selectedModeId={selectedGameModeId}
        stage={runLoading ? 'loading-current' : runSetup.stage}
        preview={runSetup.preview}
        error={runSetup.error}
        hubState={hubState}
        config={config}
        onSelect={chooseRunMode}
        onReview={reviewRunMode}
        onStart={startSelectedRun}
        onRetryCurrent={() => loadOnlineRun(selectedGameModeId, false)}
        onSignOut={signOut}
        onPlayOffline={playOffline}
      />
    );
  }

  if (gameOver) {
    const finalScore = gameState.cash + gameState.bankCash - gameState.debt;
    return (
      <div className="game-over-overlay">
        <div className="glass-panel game-over-modal">
          <h2>Game Over</h2>
          {gameOverReason === 'health' && (
            <p style={{color: 'var(--accent-red)', fontSize: '1.2rem', marginBottom: '1rem'}}>{gameModeForState(gameState).id === 'career' ? `Your Career ended on Day ${gameState.day}. Highest residence: ${gameState.career?.residence?.name || gameState.career?.residenceId || 'Dumpster'}.` : 'You died in the streets.'}</p>
          )}
          {gameOverReason === 'debt_deadline' && (
            <p style={{color: 'var(--accent-red)', fontSize: '1.2rem', marginBottom: '1rem'}}>The original debt was still alive after day {gameModeForState(gameState).debtDeadline}. Long Haul ended before the career phase.</p>
          )}
          {gameOverReason === 'day_limit' && (
            <p style={{fontSize: '1.2rem', marginBottom: '1rem'}}>You completed the {gameModeForState(gameState).title} across {gameState.maxDays} days.</p>
          )}
          {gameModeForState(gameState).id === 'career' && <>
            <div className="stat-row"><span className="stat-label">Days Survived:</span><span className="stat-value">{gameState.day}</span></div>
            <div className="stat-row"><span className="stat-label">Final Residence:</span><span className="stat-value">{gameState.career?.residence?.name || gameState.career?.residenceId || 'Dumpster'}</span></div>
            <div className="stat-row"><span className="stat-label">Street Rep:</span><span className="stat-value">{gameState.streetRep}</span></div>
            <div className="stat-row"><span className="stat-label">Highest Net Worth:</span><span className="stat-value success">${Number(gameState.career?.highestNetWorth || 0).toLocaleString()}</span></div>
          </>}
          <div className="stat-row"><span className="stat-label">Final Cash:</span><span className="stat-value success">${gameState.cash}</span></div>
          <div className="stat-row"><span className="stat-label">Bank Cash:</span><span className="stat-value success">${gameState.bankCash}</span></div>
          <div className="stat-row"><span className="stat-label">Final Debt:</span><span className="stat-value danger">${gameState.debt}</span></div>
          <div className="stat-row" style={{marginTop: '1rem', borderTop: '1px solid var(--panel-border)', paddingTop: '1rem'}}>
            <span className="stat-label">Total Score:</span>
            <span className={`stat-value ${finalScore >= 0 ? 'success' : 'danger'}`}>${finalScore}</span>
          </div>
          <button className="btn" style={{marginTop: '2rem', width: '100%'}} onClick={restartRun} disabled={actionBusy}>Choose Next Run</button>
        </div>
      </div>
    );
  }

  const loc = getCurrentLocation();
  const serverRules = gameState.serverRules || {};
  const openV2Window = (id) => {
    if (effectiveInterface !== 'v2_preview' || typeof window === 'undefined') return false;
    window.dispatchEvent(new CustomEvent('dopeman-v2-open-window', { detail: { id } }));
    return true;
  };
  const borrowingAllowed = serverRules.allow_borrowing !== false;
  const bankingAllowed = serverRules.allow_banking !== false;
  const stashAllowed = serverRules.allow_stash !== false;
  const farmUnlockCash = Number(serverRules.farm_unlock_cash ?? 5000);

  const sharedActions = {
    borrow: borrowMoney,
    payDebt,
    bank: bankAction,
    stash: stashAction,
    stashAll: stashAllAction,
    sellBarter,
    buyTravelGear,
    buySmokes,
    buyBeer,
    buyScratchers,
    leaveCornerStore,
    openCornerStoreBoard,
    drinkBeer,
    sellSmokes,
    kenSellBud,
    kenBuyMeth,
    kenTradeJunk,
    kenLookAround,
    kenGiveWifeBeer,
    mikeySell,
    robSmoke,
    robBongRip,
    robEndSession,
    openHustles: () => openV2Window('hustles') || setHustleOpen(true),
    openV2Window,
    hustleSearch,
    careerHomeWait,
    careerHomeOfferAccept,
    careerHomeOfferDecline,
    buy: handleBuy,
    sell: handleSell,
    sellAll: handleSellAll,
    travel,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    openServerInfo: () => refreshServerInfo({ open: true }),
    closeServerInfo: () => setServerInfoOpen(false),
    refreshServerInfo: () => refreshServerInfo({ open: false }),
    toggleAdventureDock: () => setAdventureDockPreference('adventure_log_panel', (uiPreferences.effective?.adventure_log_panel || 'compact') === 'hidden' ? 'compact' : 'hidden'),
    setAdventureDockPanel: (value) => setAdventureDockPreference('adventure_log_panel', value),
    setAdventureDockFilter: (value) => setAdventureDockPreference('adventure_log_filter', value),
    adventureDockUnread,
    useStableInterface: switchToStableInterface,
    returnToPreviewInterface,
    reconnect,
    signOut,
    loadLeaderboard,
    setLeaderboardVisibility,
    openLoanShark: () => setCareerLoanOpen(true),
    buyResidence: careerBuyResidence,
    lookAround: () => performAction('world_look_around', {}),
  };

  const bugContext = {
    client_version: config.appVersion,
    hub_version: hubVersion,
    interface: effectiveInterface,
    theme: effectiveTheme,
    browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
    viewport: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight, pixel_ratio: window.devicePixelRatio } : {},
    current_view: typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') || 'street' : 'street',
    current_location: gameState.currentLocationId,
    run_id: runMeta?.runId || '',
    run_revision: runMeta?.revision ?? null,
    event_pack: gameState.eventPack || runMeta?.ruleset || '',
    game_mode: gameModeForState(gameState).id,
    game_mode_phase: gameModeForState(gameState).phase,
    game_day: gameState.day,
    last_failed_endpoint: lastRequestFailure?.endpoint || '',
    last_failed_status: lastRequestFailure?.status || 0,
    last_failed_code: lastRequestFailure?.code || '',
    connection_state: hubState,
    recent_street_log: (gameState.messages || []).slice(0, 12),
    recent_client_errors: lastRequestFailure ? [`${lastRequestFailure.code}: ${lastRequestFailure.message}`] : [],
  };

  const accountSettingsWindowProps = session ? {
    hub,
    session,
    uiPreferences,
    bugContext,
    gameModes,
    currentGameModeId: gameModeForState(gameState).id,
    onUiPreferencesChanged: setUiPreferences,
    onSessionChanged: setSession,
    onRunReset: handleSelfReset,
    onSignOut: signOut,
  } : null;

  const GameInterface = effectiveInterface === 'legacy' ? LegacyGame : effectiveInterface === 'v2_preview' ? GameShellV2 : ResponsiveGame;

  const dismissV2SpecialtyScene = () => {
    if (effectiveInterface === 'v2_preview') setDismissedSpecialtySceneId(gameState.currentLocationId || 'special');
  };

  const specialtySceneActions = effectiveInterface === 'v2_preview'
    ? {
        ...sharedActions,
        travel: async (...args) => { dismissV2SpecialtyScene(); return sharedActions.travel(...args); },
        leaveCornerStore: async (...args) => { dismissV2SpecialtyScene(); return sharedActions.leaveCornerStore(...args); },
      }
    : sharedActions;

  const frameSceneForV2 = (scene, sceneId = gameState.currentLocationId) => {
    if (effectiveInterface !== 'v2_preview') return scene;
    if (dismissedSpecialtySceneId === sceneId) return null;
    return <V2SceneFrame locationName={loc?.name || 'Special Stop'} onExit={dismissV2SpecialtyScene}>{scene}</V2SceneFrame>;
  };

  if (maintenance?.enabled) {
    return <MaintenancePage maintenance={maintenance} busy={serverInfoBusy} onRetry={() => refreshServerInfo()} />;
  }

  return (
    <>
      {renderPromptModal()}
      {renderEventModal()}
      {sceneOutcome && <SceneOutcome outcome={sceneOutcome} onContinue={() => setSceneOutcome(null)} />}
      {gameModeForState(gameState).id === 'career' && (
        <CareerLoanSharkScene
          open={careerLoanOpen}
          career={gameState.career}
          cash={gameState.cash}
          streetRep={gameState.streetRep}
          busy={actionBusy}
          onBorrow={careerBorrow}
          onPay={careerPay}
          onClose={() => setCareerLoanOpen(false)}
        />
      )}
      <HustleModal
        open={hustleOpen}
        hustles={gameState.hustles}
        busy={actionBusy}
        onSearch={hustleSearch}
        onClose={() => setHustleOpen(false)}
      />
      {!gameState.activeEvent && !sceneOutcome && !settingsOpen && !careerLoanOpen && !hustleOpen && gameState.career?.milestoneCelebration?.pending && (
        <CareerMilestoneCelebration
          celebration={gameState.career.milestoneCelebration}
          busy={actionBusy}
          reducedMotion={Boolean(effectivePresentation.reduced_motion)}
          onContinue={acknowledgeCareerMilestone}
        />
      )}
      {!gameState.activeEvent && !sceneOutcome && !settingsOpen && gameState.gameMode?.debtCelebration?.pending && (
        <DebtClearedCelebration
          celebration={gameState.gameMode.debtCelebration}
          busy={actionBusy}
          reducedMotion={Boolean(effectivePresentation.reduced_motion)}
          onContinue={acknowledgeDebtCelebration}
        />
      )}
      {effectiveInterface !== 'v2_preview' && settingsOpen && session && (
        <AccountSettings
          hub={hub}
          session={session}
          uiPreferences={uiPreferences}
          bugContext={bugContext}
          gameModes={gameModes}
          currentGameModeId={gameModeForState(gameState).id}
          onUiPreferencesChanged={setUiPreferences}
          onSessionChanged={setSession}
          onRunReset={handleSelfReset}
          onClose={() => setSettingsOpen(false)}
          onSignOut={signOut}
        />
      )}
      {effectiveInterface !== 'v2_preview' && <ServerInfoModal
        open={serverInfoOpen}
        info={serverInfo}
        clientVersion={config.appVersion}
        busy={serverInfoBusy}
        onRefresh={() => refreshServerInfo()}
        onClose={() => setServerInfoOpen(false)}
      />}
      {effectiveInterface === 'v2_preview' ? <V2ErrorBoundary onStable={() => setSessionInterfaceOverride('responsive')}><GameInterface
        gameState={gameState}
        runMeta={runMeta}
        config={config}
        onlineMode={onlineMode}
        session={session}
        hubState={hubState}
        hubMessage={hubMessage}
        actionBusy={actionBusy}
        actionLabel={actionLabel}
        uiSignal={uiSignal}
        uiSettings={uiPreferences.effective}
        settingsOpen={settingsOpen}
        serverInfoOpen={serverInfoOpen}
        accountSettingsProps={accountSettingsWindowProps}
        serverInfo={serverInfo}
        serverInfoBusy={serverInfoBusy}
        loc={loc}
        borrowingAllowed={borrowingAllowed}
        bankingAllowed={bankingAllowed}
        stashAllowed={stashAllowed}
        farmUnlockCash={farmUnlockCash}
        selectedWeights={selectedWeights}
        setSelectedWeights={setSelectedWeights}
        actions={sharedActions}
      /></V2ErrorBoundary> : <GameInterface
        gameState={gameState}
        runMeta={runMeta}
        config={config}
        onlineMode={onlineMode}
        session={session}
        hubState={hubState}
        hubMessage={hubMessage}
        actionBusy={actionBusy}
        actionLabel={actionLabel}
        uiSignal={uiSignal}
        uiSettings={uiPreferences.effective}
        settingsOpen={settingsOpen}
        serverInfoOpen={serverInfoOpen}
        accountSettingsProps={accountSettingsWindowProps}
        serverInfo={serverInfo}
        serverInfoBusy={serverInfoBusy}
        loc={loc}
        borrowingAllowed={borrowingAllowed}
        bankingAllowed={bankingAllowed}
        stashAllowed={stashAllowed}
        farmUnlockCash={farmUnlockCash}
        selectedWeights={selectedWeights}
        setSelectedWeights={setSelectedWeights}
        actions={sharedActions}
      />}
      {preferredInterface === 'v2_preview' && effectiveInterface === 'responsive' && <div className="v2-session-fallback"><span>UI v2 preference is still active · using Stable UI for this session</span><button onClick={returnToPreviewInterface}>RETURN TO V2 PREVIEW</button></div>}
      {effectiveInterface !== 'v2_preview' && uiPreferences.effective?.adventure_log_enabled !== false && !settingsOpen && !gameState.activeEvent && !sceneOutcome && !careerLoanOpen && !hustleOpen && !loc?.isSceneLocation && !loc?.isPawnShop && gameState.currentLocationId !== 'cavemans_house' && (
        <AdventureLogDock
          gameState={gameState}
          enabled
          panel={uiPreferences.effective?.adventure_log_panel || 'compact'}
          filter={uiPreferences.effective?.adventure_log_filter || 'all'}
          unread={adventureDockUnread}
          onPanelChange={(value) => setAdventureDockPreference('adventure_log_panel', value)}
          onFilterChange={(value) => setAdventureDockPreference('adventure_log_filter', value)}
          onOpenFull={() => switchToStableInterface('more')}
        />
      )}
      {gameState.currentLocationId === 'corner_store' && !gameState.activeEvent && (
        frameSceneForV2(<CornerStoreScene gameState={gameState} busy={actionBusy} actions={specialtySceneActions} />, 'corner_store')
      )}
      {gameState.currentLocationId === 'kens_house' && !gameState.activeEvent && (
        frameSceneForV2(<KenHouseScene gameState={gameState} busy={actionBusy} actions={specialtySceneActions} />, 'kens_house')
      )}
      {gameState.currentLocationId === 'mikeys_place' && !gameState.activeEvent && (
        frameSceneForV2(<MikeysPlaceScene gameState={gameState} busy={actionBusy} actions={specialtySceneActions} />, 'mikeys_place')
      )}
      {gameState.currentLocationId === 'robs_trailer' && !gameState.activeEvent && (
        frameSceneForV2(<RobHippieScene gameState={gameState} busy={actionBusy} actions={specialtySceneActions} />, 'robs_trailer')
      )}
      {gameState.currentLocationId === 'cavemans_house' && !gameState.activeEvent && packAtLeast(gameState.eventPack, 10) && (
        frameSceneForV2(<CavemanHouseScene gameState={gameState} onDismiss={effectiveInterface === 'v2_preview' ? dismissV2SpecialtyScene : undefined} />, 'cavemans_house')
      )}
      {gameState.currentLocationId === 'pawn_shop' && !gameState.activeEvent && (
        frameSceneForV2(<PawnShopScene gameState={gameState} busy={actionBusy} actions={specialtySceneActions} onDismiss={effectiveInterface === 'v2_preview' ? dismissV2SpecialtyScene : undefined} />, 'pawn_shop')
      )}
    </>
  );

}

export default App;
